import { useEffect } from "react";

/**
 * Lightweight per-page SEO manager (SPA / no SSR).
 * Sets <title>, meta description, canonical, and OG basics; restores the
 * original values on unmount so navigating back to Home doesn't leak
 * service-page copy.
 */
export default function useSeo({ title, description, canonical, robots = "index, follow, max-image-preview:large" }) {
  useEffect(() => {
    const prevTitle = document.title;
    const prevDesc = getMetaContent('meta[name="description"]');
    const prevCanonical = document.querySelector('link[rel="canonical"]')?.getAttribute("href") || null;
    const prevRobots = getMetaContent('meta[name="robots"]');
    const prevOgTitle = getMetaContent('meta[property="og:title"]');
    const prevOgDesc = getMetaContent('meta[property="og:description"]');
    const prevOgUrl = getMetaContent('meta[property="og:url"]');

    if (title) document.title = title;
    if (description) setMeta('meta[name="description"]', "name", "description", description);
    if (canonical) setLink("canonical", canonical);
    if (robots) setMeta('meta[name="robots"]', "name", "robots", robots);
    if (title) setMeta('meta[property="og:title"]', "property", "og:title", title);
    if (description) setMeta('meta[property="og:description"]', "property", "og:description", description);
    if (canonical) setMeta('meta[property="og:url"]', "property", "og:url", canonical);

    return () => {
      document.title = prevTitle;
      if (prevDesc != null) setMeta('meta[name="description"]', "name", "description", prevDesc);
      if (prevRobots != null) setMeta('meta[name="robots"]', "name", "robots", prevRobots);
      if (prevCanonical) setLink("canonical", prevCanonical);
      if (prevOgTitle != null) setMeta('meta[property="og:title"]', "property", "og:title", prevOgTitle);
      if (prevOgDesc != null) setMeta('meta[property="og:description"]', "property", "og:description", prevOgDesc);
      if (prevOgUrl != null) setMeta('meta[property="og:url"]', "property", "og:url", prevOgUrl);
    };
  }, [title, description, canonical, robots]);
}

function getMetaContent(selector) {
  const el = document.querySelector(selector);
  return el ? el.getAttribute("content") : null;
}

function setMeta(selector, attr, attrVal, content) {
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, attrVal);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel, href) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/**
 * Inject a page-specific JSON-LD schema script. Removes it on unmount.
 */
export function useJsonLd(schemaObj, key = "page-schema") {
  useEffect(() => {
    if (!schemaObj) return;
    const id = `jsonld-${key}`;
    let el = document.getElementById(id);
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = id;
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(schemaObj);
    return () => {
      const node = document.getElementById(id);
      if (node) node.remove();
    };
  }, [key, schemaObj]);
}
